import { Card } from '@/components/base/Card';
import { Button } from '@/components/base/Button';
import { Badge } from '@/components/base/Badge';
import { Skeleton } from '@/components/base/Skeleton';
import { User, Mail, Phone, CalendarClock, Award, Cake, UserCircle } from 'lucide-react';

export interface Qualification {
  code: string;
  display: string;
  system?: string;
}

export interface PractitionerViewProps {
  practitioner?: {
    id: string;
    name: string;
    gender?: string;
    birthDate?: string;
    age?: number;
    qualifications?: Qualification[];
    email?: string;
    phone?: string;
    status: string;
    identifier?: string;
    createdAt?: string;
    updatedAt?: string;
  };
  isLoading: boolean;
  error?: string | null;
  onBack: () => void;
  onEdit?: () => void;
}

export const PractitionerView: React.FC<PractitionerViewProps> = ({
  practitioner,
  isLoading,
  error,
  onBack,
  onEdit,
}) => {
  if (isLoading) {
    return (
      <div className="py-6">
        <div className="mx-auto max-w-4xl">
          <Skeleton className="mb-6 h-8 w-48" />
          <Card className="p-6">
            <Skeleton className="mb-4 h-6 w-32" />
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-6">
        <div className="mx-auto max-w-4xl">
          <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/10 p-4">
            <p className="text-[calc(13px*var(--app-scale))] text-destructive">{error}</p>
          </div>
          <Button onClick={onBack} variant="outline">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (!practitioner) {
    return (
      <div className="py-6">
        <div className="mx-auto max-w-4xl">
          <div className="py-12 text-center">
            <p className="text-[calc(13px*var(--app-scale))] text-muted-foreground">
              Practitioner not found
            </p>
            <Button onClick={onBack} variant="outline" className="mt-4">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-[calc(32px*var(--app-scale))] font-medium leading-[calc(40px*var(--app-scale))] tracking-tight text-pranik-brand-700">
              {practitioner.name}
            </h1>
            <p className="mt-1 font-hanken text-[calc(18px*var(--app-scale))] font-normal leading-[calc(18px*var(--app-scale))] tracking-label-2 text-gray-700">
              Practitioner Details
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={onBack} variant="outline">
              Back
            </Button>
            {onEdit && (
              <Button onClick={onEdit} variant="primary">
                Edit
              </Button>
            )}
          </div>
        </div>

        {/* Main Information Card */}
        <Card className="mb-6 p-6">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="font-hanken text-[calc(24px*var(--app-scale))] font-medium leading-[calc(28px*var(--app-scale))] tracking-normal text-gray-700">
              Basic Information
            </h2>
            <Badge variant={practitioner.status === 'Active' ? 'default' : 'secondary'}>
              {practitioner.status}
            </Badge>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Name */}
            <div className="flex items-start gap-3">
              <div className="mt-1">
                <User className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-muted-foreground" />
              </div>
              <div className="flex-1">
                <p className="text-[calc(13px*var(--app-scale))] font-medium text-gray-500">
                  Full Name
                </p>
                <p className="mt-1 text-[calc(18px*var(--app-scale))] text-gray-900">
                  {practitioner.name}
                </p>
              </div>
            </div>

            {/* Gender */}
            {practitioner.gender && (
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  <UserCircle className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-[calc(13px*var(--app-scale))] font-medium text-gray-500">
                    Gender
                  </p>
                  <p className="mt-1 text-[calc(18px*var(--app-scale))] capitalize text-gray-900">
                    {practitioner.gender}
                  </p>
                </div>
              </div>
            )}

            {/* Birth Date & Age */}
            {practitioner.birthDate && (
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  <Cake className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-[calc(13px*var(--app-scale))] font-medium text-gray-500">
                    Date of Birth
                  </p>
                  <p className="mt-1 text-[calc(18px*var(--app-scale))] text-gray-900">
                    {practitioner.birthDate}
                    {practitioner.age !== undefined && (
                      <span className="ml-2 text-[calc(13px*var(--app-scale))] text-gray-500">
                        ({practitioner.age} years old)
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* Email */}
            {practitioner.email && (
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  <Mail className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-[calc(13px*var(--app-scale))] font-medium text-gray-500">
                    Email
                  </p>
                  <p className="mt-1 text-[calc(18px*var(--app-scale))] text-gray-900">
                    <a
                      href={`mailto:${practitioner.email}`}
                      className="text-primary hover:underline"
                    >
                      {practitioner.email}
                    </a>
                  </p>
                </div>
              </div>
            )}

            {/* Phone */}
            {practitioner.phone && (
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  <Phone className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-[calc(13px*var(--app-scale))] font-medium text-gray-500">
                    Phone
                  </p>
                  <p className="mt-1 text-[calc(18px*var(--app-scale))] text-gray-900">
                    <a href={`tel:${practitioner.phone}`} className="text-primary hover:underline">
                      {practitioner.phone}
                    </a>
                  </p>
                </div>
              </div>
            )}

            {/* Created At */}
            {practitioner.createdAt && (
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  <CalendarClock className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-[calc(13px*var(--app-scale))] font-medium text-gray-500">
                    Registered On
                  </p>
                  <p className="mt-1 text-[calc(18px*var(--app-scale))] text-gray-900">
                    {practitioner.createdAt}
                  </p>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Qualifications */}
        {practitioner.qualifications && practitioner.qualifications.length > 0 && (
          <Card className="mb-6 p-6">
            <div className="mb-4 flex items-center gap-2">
              <Award className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-primary" />
              <h2 className="font-hanken text-[calc(24px*var(--app-scale))] font-medium leading-[calc(28px*var(--app-scale))] tracking-normal text-gray-700">
                Qualifications
              </h2>
            </div>
            <div className="space-y-3">
              {practitioner.qualifications.map((qual, index) => (
                <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-[calc(18px*var(--app-scale))] font-semibold text-gray-900">
                        {qual.display}
                      </p>
                      <p className="mt-1 font-mono text-[calc(13px*var(--app-scale))] text-gray-500">
                        Code: {qual.code}
                      </p>
                      {qual.system && (
                        <p className="mt-1 text-[calc(13px*var(--app-scale))] text-gray-500">
                          System: {qual.system}
                        </p>
                      )}
                    </div>
                    <Badge variant="default" className="ml-2">
                      Certified
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Additional Information */}
        <Card className="p-6">
          <h2 className="mb-4 font-hanken text-[calc(24px*var(--app-scale))] font-medium leading-[calc(28px*var(--app-scale))] tracking-normal text-gray-700">
            Additional Information
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between border-b py-2">
              <span className="text-[calc(13px*var(--app-scale))] text-gray-500">
                Practitioner ID
              </span>
              <span className="font-mono text-[calc(13px*var(--app-scale))] text-gray-900">
                {practitioner.id}
              </span>
            </div>
            {practitioner.updatedAt && (
              <div className="flex justify-between border-b py-2">
                <span className="text-[calc(13px*var(--app-scale))] text-gray-500">
                  Last Updated
                </span>
                <span className="text-[calc(13px*var(--app-scale))] text-gray-900">
                  {practitioner.updatedAt}
                </span>
              </div>
            )}
            <div className="flex justify-between py-2">
              <span className="text-[calc(13px*var(--app-scale))] text-gray-500">Status</span>
              <Badge variant={practitioner.status === 'Active' ? 'default' : 'secondary'}>
                {practitioner.status}
              </Badge>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PractitionerView;
